import { MapPin, Clock, ListTodo, Video } from 'lucide-react';
import type { EventInstance, TimeFormat } from '../types';
import { formatTimeRange, getEventColor, detectVideoLink } from '../utils';

interface AgendaItemProps {
  event: EventInstance;
  onClick: () => void;
  timeFormat?: TimeFormat;
  /** The day this row is listed under, so a multi-day span is labelled by its
   *  portion of THIS day rather than repeating its original start. */
  onDay?: Date;
  dimPastEvents?: boolean;
  eventColorPriority?: 'event' | 'calendar';
}

export function AgendaItem({
  event,
  onClick,
  timeFormat = '12h',
  onDay,
  dimPastEvents = false,
  eventColorPriority = 'event',
}: AgendaItemProps) {
  const color = getEventColor(event, eventColorPriority);
  const isPast = dimPastEvents && new Date(event.endAt) < new Date();
  const metadata = (event.metadata || {}) as Record<string, unknown>;
  const isFocusTime = metadata.focusTime === true;
  const isScheduledTask = typeof metadata.schedulableTaskId === 'string';
  const hasVideoCall = !!detectVideoLink(event);

  return (
    <button
      type="button"
      className={`flex items-start gap-3 px-4 py-2 hover:bg-muted/50 cursor-pointer rounded transition-colors w-full text-left ${isPast ? 'opacity-50' : ''}`}
      onClick={onClick}
    >
      {/* Color dot */}
      <div
        className="w-3 h-3 rounded-full mt-1 shrink-0"
        style={{
          backgroundColor: isFocusTime ? 'transparent' : color,
          ...(isFocusTime ? { border: `2px dashed ${color}` } : {}),
        }}
      />

      {/* Event info */}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm flex items-center gap-1 min-w-0">
          {isScheduledTask && <ListTodo className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
          {hasVideoCall && <Video className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
          <span className="truncate">{event.title}</span>
          {isFocusTime && (
            <span className="text-[10px] text-muted-foreground font-normal shrink-0">
              (flexible)
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5 min-w-0">
          <span className="flex items-center gap-1 shrink-0">
            <Clock className="h-3 w-3" />
            {formatTimeRange(event.startAt, event.endAt, event.isAllDay, timeFormat, onDay)}
          </span>
          {event.location && (
            <span className="flex items-center gap-1 min-w-0">
              <MapPin className="h-3 w-3 shrink-0" />
              <span className="truncate">{event.location}</span>
            </span>
          )}
        </div>
      </div>

      {/* Calendar name */}
      {event.calendarName && (
        <span className="text-[10px] text-muted-foreground shrink-0 max-w-[6rem] truncate">
          {event.calendarName}
        </span>
      )}
    </button>
  );
}
